import { Fragment, useEffect, useMemo, useState } from "react";
import ListView from "@/common/custom-lists/ListView";
import { connect } from "react-redux";
import Select from "react-select";
import { Actions as FSS_REPORT_ACTIONS } from "@/redux/actions/fssReportActions";
import InvalidPermissions from "../company-pages/InvalidPermissions";
import EIDLoginModal from "./EIDLoginModal";
import FSSExpressRow from "./FSSExpressRow";
import { NotificationManager } from "react-notifications";
import { differenceInCalendarYears } from "date-fns";
import { CustomOption } from "@/common/custom-selectors/CustomOption";
import Styles from "./EIDStyle.module.css";
import Tooltip from "@/tooltip";
import FS7ValidationModal from "./FS7ValidationModal";

const SubmitButton = ({ loggedIn, noAccess, selectedCompanies, validated, onClick }) => {
	const [content, tooltipStatus] = useMemo(() => {
		let content = "";
		if (!loggedIn) content = "Sign in to submit";
		else if (!selectedCompanies?.length) content = "Select companies to submit";
		else if (noAccess) content = "Some companies are not authenticated";
		else if (validated) content = "Validate companies before submitting";

		return [content, !!content];
	}, [noAccess, selectedCompanies?.length, validated]);

	return (
		<button
			type="button"
			className="btn btn-primary"
			onClick={onClick}
			disabled={tooltipStatus}
		>
			<Tooltip
				content={content}
				isOpen={tooltipStatus ? undefined : false}
				arrowSize={4}
				distance={10}
			>
				Submit
			</Tooltip>
		</button>
	);
};

const ValidateButton = ({ loggedIn, noAccess, selectedCompanies, onClick }) => {
	const [content, tooltipStatus] = useMemo(() => {
		let content = "";
		if (!loggedIn) content = "Sign in to validate";
		else if (!selectedCompanies?.length) content = "Select companies to validate";
		// else if (noAccess) content = "Some companies are not authenticated";

		return [content, !!content];
	}, [noAccess, selectedCompanies?.length, loggedIn]);

	return (
		<button
			type="button"
			className="btn btn-default"
			onClick={onClick}
			disabled={tooltipStatus}
		>
			<Tooltip
				content={content}
				isOpen={tooltipStatus ? undefined : false}
				arrowSize={4}
				distance={10}
			>
				Validate
			</Tooltip>
		</button>
	);
};

const FSSExpress = ({
	reportTypes,
	fssExpress,
	yearOptions,
	userPermissions,
	getFSSCompanies,
	forAdvisor,
	openEIDModal,
	updateSelectedCompanies,
	selectedCompanies,
	clearSelectedCompanies,
	loggedIn,
	validateFS7,
	submitFS7,
}) => {
	const [reportType, setReportType] = useState(reportTypes[0]?.options[0]);
	const [year, setYear] = useState(yearOptions?.find(item => item.value == new Date().getFullYear()- 1));

	const [noAccess, validated] = useMemo(() => {
		const noAccess = selectedCompanies?.find((company) => !company?.authenticated);
		const validated = fssExpress
			?.filter(({ peNumber }) => selectedCompanies?.find((p) => p.peNumber == peNumber))
			?.find((company) => company?.status == "Validated");
		return [!!noAccess, validated];
	}, [JSON.stringify(selectedCompanies)]);

	const submitFSSXpress = (test) => {
		if (!selectedCompanies?.length) {
			NotificationManager.error("Please select companies to submit");
			return;
		}

		const functionToCall = test ? validateFS7 : submitFS7;
		functionToCall(year?.value);
	};

	useEffect(() => {
		if (yearOptions && !year) setYear(yearOptions[yearOptions.length - 1]);
	}, [yearOptions]);

	useEffect(() => {
		getFSSCompanies(reportType?.value, year?.value, forAdvisor);
		return () => clearSelectedCompanies();
	}, [year, forAdvisor]);

	useEffect(() => {
		clearSelectedCompanies()
	}, [year?.value])

	// console.log('connected',connected,connectLink)
	if (!forAdvisor && userPermissions == "None") return <InvalidPermissions />;

	return (
		<>
			<FS7ValidationModal forFSSExpress={true} year={year?.value} />
			<EIDLoginModal forBulkSubmission={true} year={year?.value}  />
			<div className="content bg-gray-lighter">
				<div className="row">
					<div className="col-sm-12">
						<div className="pull-left">
							<h1 className="page-heading">FSSXpress™</h1>
						</div>
					</div>
				</div>
			</div>

			<div className="content bg-white">
				<div className="block-header pr-0 pl-0">
					<div className="col-xs-3 pl-0">
						<label htmlFor="material-text">Tax Form</label>
						<Select
							components={{ Option: CustomOption }}
							styles={{
								menu: (provided) => ({ ...provided, zIndex: 10 }),
							}}
							value={reportType}
							options={reportTypes}
							onChange={setReportType}
						/>
						<div className="clearfix" />
					</div>
					<div className="col-xs-3 pl-0">
						<label htmlFor="material-text">Year</label>
						<Select
							value={year}
							styles={{
								menu: (provided) => ({ ...provided, zIndex: 10 }),
							}}
							options={yearOptions}
							onChange={setYear}
						/>
					</div>
					<div className="col-xs-3 pl-0">
						<div className="pull-right">
							<label>e-ID</label>
							<div className="btn-group">
								<button
									type="button"
									className={`btn ${Styles["btn-eid"]}`}
									disabled={!selectedCompanies?.length}
									onClick={() => {
										openEIDModal();
									}}
								>
									Sign In
								</button>
							</div>
						</div>
					</div>
					<div className="col-xs-3 pl-0">
						<div className="pull-right">
							<label>FSSXpress™</label>
							<div className="btn-group">
								<ValidateButton
									{...{
										loggedIn,
										noAccess,
										selectedCompanies,
									}}
									onClick={() => {
										submitFSSXpress(true);
									}}
								/>
								<SubmitButton
									{...{
										loggedIn,
										noAccess,
										selectedCompanies,
										validated,
									}}
									onClick={() => {
										submitFSSXpress();
									}}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="clearfix" />

				<ListView
					{...{
						title: reportType?.label + "s",
						items: [],
						columns: [
							{ hidden: true },
							"PE Number",
							"Organisation",
							"Employee Count",
							"Payment Due",
							"Status",
							" ",
						],
						items: fssExpress?.map?.((fss) => {
							return (
								<FSSExpressRow
									{...{
										...fss,
										onSelect: (checked, peNumber) => {
											updateSelectedCompanies(checked, peNumber);
										},
									}}
								/>
							);
						}),
					}}
				/>
			</div>
			<div className="clearfix" />
		</>
	);
};

const mapStateToProps = (state) => {
	const reportTypes = [
		{
			label: "Yearly",
			options: [
				{
					label: "FS7",
					value: "fs7",
				},
			],
		},
		// {
		// 	label: "Monthly",
		// 	options: [
		// 		{
		// 			label: "FS5",
		// 			value: "fs5",
		// 			isDisabled: true,
		// 			description: "Coming soon",
		// 		},
		// 	],
		// },
	];
	const yearOptions = [...Array(differenceInCalendarYears(new Date(), new Date(2022, 1, 1)) + 1)].map(
		(item, index) => ({
			value: `${2022 + index}`,
			label: `${2022 + index}`,
			isDisabled: 2022 + index < 2022,
		}),
	);

	const showV2Reports = state.showV2Reports;
	const userPermissions = state?.permissions?.my?.permissions?.taxForms;

	const fssExpress = state.fssExpress?.organisations;

	return {
		showV2Reports,
		reportTypes,
		yearOptions,
		// fssExpress: state.fssExpress,
		selectedCompanies: state?.fssExpress?.selectedOrganisations || [],
		fssExpress,
		userPermissions,
		org: state?.company?.settings?.org,
		isAdmin: state?.my?.profile?.isAdmin,
		loggedIn: !!state.submissionForm?.credentials,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getFSSCompanies: (type, year, advisor) => dispatch(FSS_REPORT_ACTIONS.getFSSCompanies({ type, year, advisor })),
	validateFS7: (year) => dispatch(FSS_REPORT_ACTIONS.validateFS7({ year })),
	submitFS7: (year) => dispatch(FSS_REPORT_ACTIONS.validateFS7({ year, submit: true })),
	openEIDModal: () => dispatch(FSS_REPORT_ACTIONS.openEIDModal(true)),
	updateSelectedCompanies: (checked, peNumber) =>
		dispatch(FSS_REPORT_ACTIONS.updateSelectedCompanies(checked, peNumber)),
	clearSelectedCompanies: () => dispatch(FSS_REPORT_ACTIONS.updateSelectedCompanies(undefined, undefined, true)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FSSExpress);
